import { apiClient } from './client.js'

// ==================== STOCKS (Persediaan) ====================

/**
 * Get list of stocks with pagination and filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.query - Search query
 * @param {boolean} params.low_stock - Filter low stock items
 * @param {string} params.warehouse_id - Filter by warehouse
 * @param {string} params.category_id - Filter by category
 */
export const getStocks = async (params = {}) => {
  const query = new URLSearchParams()
  if (params.page) query.append('page', params.page)
  if (params.limit) query.append('limit', params.limit)
  if (params.query) query.append('query', params.query)
  if (params.low_stock !== undefined) query.append('low_stock', params.low_stock)
  if (params.warehouse_id) query.append('warehouse_id', params.warehouse_id)
  if (params.category_id) query.append('category_id', params.category_id)
  if (params.use_service !== undefined) query.append('use_service', params.use_service)
  if (params.product_ids) query.append('product_ids', params.product_ids)
  
  return apiClient.get(`/inventory/stocks?${query.toString()}`)
}

/**
 * Get stock detail by ID
 */
export const getStockDetail = async (id) => {
  return apiClient.get(`/inventory/stocks/${id}`)
}

/**
 * Update stock configuration (minimum stock, warehouse)
 */
export const updateStockConfig = async (id, data) => {
  return apiClient.put(`/inventory/stocks/${id}/config`, data)
}

/**
 * Get stock mutation history (kartu stok)
 */
export const getStockMutations = async (id, params = {}) => {
  const query = new URLSearchParams()
  if (params.page) query.append('page', params.page)
  if (params.limit) query.append('limit', params.limit)
  if (params.start_date) query.append('start_date', params.start_date)
  if (params.end_date) query.append('end_date', params.end_date)
  
  return apiClient.get(`/inventory/stocks/${id}/mutations?${query.toString()}`)
}

// ==================== STOCK ADJUSTMENTS / STOCK OPNAME (Penyesuaian) ====================

/**
 * Get list of stock adjustments
 */
export const getStockAdjustments = async (params = {}) => {
  const query = new URLSearchParams()
  if (params.page) query.append('page', params.page)
  if (params.limit) query.append('limit', params.limit)
  const statusValue = Array.isArray(params.status) ? params.status.filter(Boolean).join(',') : params.status
  if (statusValue) query.append('status', statusValue)
  if (params.start_date) query.append('start_date', params.start_date)
  if (params.end_date) query.append('end_date', params.end_date)

  return apiClient.get(`/inventory/adjustments?${query.toString()}`)
}

/**
 * Create new stock adjustment (draft)
 * @param {{ date: string, notes?: string, attachment_url?: string, items: Array<{ product_id: string, variant_id?: string|null, type: 'IN'|'OUT', qty: number, reason?: string }> }} data
 */
export const createStockAdjustment = async (data) => {
  return apiClient.post('/inventory/adjustments', data)
}

/**
 * Get stock adjustment detail
 */
export const getStockAdjustmentDetail = async (id) => {
  return apiClient.get(`/inventory/adjustments/${id}`)
}

/**
 * Update stock adjustment (only when status is draft)
 */
export const updateStockAdjustment = async (id, data) => {
  return apiClient.put(`/inventory/adjustments/${id}`, data)
}

/**
 * Approve or reject a stock adjustment
 * @param {string} id
 * @param {'APPROVED'|'REJECTED'} status
 */
export const updateAdjustmentStatus = async (id, status) => {
  return apiClient.put(`/inventory/adjustments/${id}/status`, { status })
}

/**
 * Delete stock adjustment (only when status is draft)
 */
export const deleteStockAdjustment = async (id) => {
  return apiClient.delete(`/inventory/adjustments/${id}`)
}

// ==================== STOCK MOVEMENTS (Audit Trail) ====================

/**
 * Get stock movements
 */
export const getStockMovements = async (params = {}) => {
  const query = new URLSearchParams()
  if (params.page) query.append('page', params.page)
  if (params.limit) query.append('limit', params.limit)
  if (params.product_id) query.append('product_id', params.product_id)
  if (params.reference_type) query.append('reference_type', params.reference_type)
  if (params.start_date) query.append('start_date', params.start_date)
  if (params.end_date) query.append('end_date', params.end_date)

  return apiClient.get(`/stock-movements?${query.toString()}`)
}

// ==================== PURCHASE ORDERS (Permintaan & Pengadaan) ====================

/**
 * Get list of purchase orders
 */
export const getPurchaseOrders = async (params = {}) => {
  const query = new URLSearchParams()
  if (params.page) query.append('page', params.page)
  if (params.limit) query.append('limit', params.limit)
  const statusValue = Array.isArray(params.status) ? params.status.filter(Boolean).join(',') : params.status
  if (statusValue) query.append('status', statusValue)
  if (params.supplier_id) query.append('supplier_id', params.supplier_id)
  
  return apiClient.get(`/inventory/purchase-orders?${query.toString()}`)
}

/**
 * Create new purchase order
 */
export const createPurchaseOrder = async (data) => {
  return apiClient.post('/inventory/purchase-orders', data)
}

/**
 * Get purchase order detail
 */
export const getPurchaseOrderDetail = async (id) => {
  return apiClient.get(`/inventory/purchase-orders/${id}`)
}

/**
 * Update purchase order (only if status is DRAFT / SUBMITTED)
 */
export const updatePurchaseOrder = async (id, data) => {
  return apiClient.put(`/inventory/purchase-orders/${id}`, data)
}

/**
 * Update purchase order status
 */
export const updatePurchaseOrderStatus = async (id, data) => {
  return apiClient.put(`/inventory/purchase-orders/${id}/status`, data)
}

/**
 * Delete purchase order (only if status is DRAFT)
 */
export const deletePurchaseOrder = async (id) => {
  return apiClient.delete(`/inventory/purchase-orders/${id}`)
}

// ==================== RECEIVINGS (Penerimaan) ====================

/**
 * Get list of receivings
 */
export const getReceivings = async (params = {}) => {
  const query = new URLSearchParams()
  if (params.page) query.append('page', params.page)
  if (params.limit) query.append('limit', params.limit)
  if (params.po_number) query.append('po_number', params.po_number)
  
  return apiClient.get(`/inventory/receivings?${query.toString()}`)
}

/**
 * Create new receiving
 * @param {{
 *   po_id: string,
 *   received_date: string,
 *   notes?: string,
 *   attachments?: string[],
 *   items: Array<{
 *     product_id: string,
 *     quantity: number,
 *     unit_price: number,
 *     condition: 'GOOD' | 'DAMAGED' | 'EXPIRED'
 *   }>
 * }} data
 */
export const createReceiving = async (data) => {
  return apiClient.post('/inventory/receivings', data)
}

/**
 * Get receiving detail
 */
export const getReceivingDetail = async (id) => {
  return apiClient.get(`/inventory/receivings/${id}`)
}

/**
 * Get receivings by PO ID
 */
export const getPurchaseOrderReceivings = async (poId) => {
  return apiClient.get(`/inventory/purchase-orders/${poId}/receivings`)
}

export const getReceivingsByPO = getPurchaseOrderReceivings;

// ==================== ADDON STOCKS (Stok Ekstra) ====================

/**
 * Get list of addon stocks (stok ekstra)
 */
export const getAddonStocks = async (params = {}) => {
  const query = new URLSearchParams()
  if (params.page) query.append('page', params.page)
  if (params.limit) query.append('limit', params.limit)
  if (params.query) query.append('query', params.query)
  return apiClient.get(`/inventory/addon-stocks?${query.toString()}`)
}

/**
 * Set addon stock to a new value (creates a mutation record)
 * @param {string} addonId - Addon UUID
 * @param {number} newStock - Target stock level
 * @param {number} minStock - Target minimum stock level
 * @param {string} notes - Optional notes
 */
export const setAddonStock = async (addonId, newStock, minStock, notes = '') => {
  return apiClient.post(`/inventory/addon-stocks/${addonId}/set`, { 
    new_stock: newStock, 
    min_stock: minStock,
    notes 
  })
}
